import React from 'react';
import styled from '@emotion/styled';
import { Button, Input, Modal, Spin } from 'antd';
import { colorConstants } from 'config/colorConstants';
import { TickOutlined } from 'assets/svg/TickOutlined';
import { loginAntIcon } from 'components/organism/Login/loginSpinner';
import type { ResetPasswordModalStatus } from 'components/organism/Login/loginViewProps';

const ModalStyled = styled(Modal)`
    .ant-modal-content {
        overflow: hidden;
        border-radius: 4px;
    }

    .ant-modal-close-x {
        line-height: 43px;
    }
    .ant-modal-header {
        border-bottom: none;
        padding-bottom: 10px;
    }
    .ant-modal-footer {
        border-top: none;
    }
`;

type Props = {
    status: ResetPasswordModalStatus;
    setStatus: React.Dispatch<React.SetStateAction<ResetPasswordModalStatus>>;
    email: string;
    setEmail: (value: string) => void;
    loading: boolean;
    onSubmit: () => void;
    inputRef: React.MutableRefObject<any>;
};

/**
 * Forgot-password flow. Shared verbatim by both login layouts — antd v4 renders
 * it into a portal, so it does not participate in either layout's box model.
 */
export const ResetPasswordModal = ({ status, setStatus, email, setEmail, loading, onSubmit, inputRef }: Props) => (
        <ModalStyled
            destroyOnClose
            maskClosable={status.step === 2 ? true : false}
            bodyStyle={{ paddingBlock: 6 }}
            onCancel={() => {
                setEmail('');
                setStatus({ ...status, step: 0, show: false });
            }}
            centered
            width={348}
            open={status.show}
            title={status.step === 1 ? 'Reset Password' : ''}
            footer={
                status.step === 1 ? (
                    <Button
                        disabled={loading}
                        style={{
                            color: colorConstants.white,
                            borderRadius: '4px',
                            backgroundColor: colorConstants.violet,
                            height: 40,
                            marginRight: 8,
                            fontWeight: 700,
                            minWidth: 132,
                            // paddingBlock: 12,
                            // display: 'flex',
                            // alignItems: 'center',
                            // justifyContent: 'center',
                        }}
                        className="!text-[13px]"
                        onClick={onSubmit}
                    >
                        {loading ? (
                            <Spin indicator={loginAntIcon} style={{ color: 'white' }} />
                        ) : (
                            'Send Reset Link'
                        )}
                    </Button>
                ) : (
                    ''
                )
            }
        >
            <>
                {status.step === 1 ? (
                    <>
                        <div className="font-xs text-promoColour">
                            To reset your password, enter the email address you use to sign in.
                        </div>
                        <div className="mt-4">
                            <Input
                                ref={inputRef}
                                placeholder="Enter your Email"
                                type={'text'}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                style={{
                                    padding: '8px 10px',
                                    // @code-guard: skip-next-line reason="login brand border color; pending design-system migration"
                                    border: '1px solid #243E51',
                                    borderRadius: '4px',
                                    // @code-guard: skip-next-line reason="login modal background; pending design-system migration"
                                    background: '#ffffff',
                                    // @code-guard: skip-next-line reason="login brand text color; pending design-system migration"
                                    color: '#243E51',
                                    width: '100%',
                                }}
                            />
                        </div>
                    </>
                ) : status.step === 2 ? (
                    <div className="flex flex-col items-center justify-center mt-6">
                        <span className="mb-4">
                            <TickOutlined />
                        </span>

                        <h2 className="font-lg text-subHeaderColor font-medium">Password Link Sent</h2>
                        {
                            <p className="text-fontGreyAlt1 font-xs mb-4">
                                Please check your inbox {email}
                            </p>
                        }
                        <div className="w-full border border-promoColour mb-2"></div>
                        <p className="text-promoColour font-xs mb-4">
                            Unsure if that email address was correct?{' '}
                            <span
                                onClick={() => {
                                    setStatus({ ...status, step: 1 });
                                }}
                                className="text-purpleFont font-medium cursor-pointer"
                            >
                                Resend.
                            </span>
                        </p>
                    </div>
                ) : null}
            </>
        </ModalStyled>
);
